import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { subMonths } from 'date-fns';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import config from 'walden-fast/config/environment';
import type FastApiService from 'walden-fast/services/fast-api';
import PowerBiService from 'walden-fast/services/power-bi';
import type UserService from 'walden-fast/services/user';
import type { EmissionDetailData } from 'walden-fast/types/fast-backend/emissions';
import type { OffsetAggregated } from 'walden-fast/types/offset-aggregated';
import type { OffsetPurchase } from 'walden-fast/types/offset-purchase';
import type { OffsetDetailData } from 'walden-fast/types/offsets';
import type {
  PaginatedResponse,
  SortDirection,
} from 'walden-fast/types/pagination';
import { SERVICE_NAMES } from 'walden-fast/utils/constants/services';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type AppServicesWaldenGreenOverviewModel = Resolved<
  ReturnType<AppServicesWaldenGreenOverviewRoute['model']>
>;

export type DateRangeOption = '1month' | '6months' | '1year';

type AppServicesWaldenGreenOverviewRouteQueryParams = {
  dateRange?: DateRangeOption;
  purchaseHistorySortKey: string | undefined;
  purchaseHistorySortDirection: SortDirection | undefined;
  purchaseHistoryPage: number | undefined;
  purchaseHistoryPageSize: number | undefined;
  clientCode?: string;
};

const rangeToMonthsMap: Record<DateRangeOption, number> = {
  '1month': 1,
  '6months': 6,
  '1year': 12,
};

export default class AppServicesWaldenGreenOverviewRoute extends Route {
  @service('fast-api') declare fastApiService: FastApiService;
  @service('intl') declare intlService: IntlService;
  @service('power-bi') declare powerBiService: PowerBiService;
  @service('router') declare routerService: RouterService;
  @service('user') declare userService: UserService;

  queryParams = {
    dateRange: {
      refreshModel: true,
    },
    purchaseHistorySortKey: {
      refreshModel: true,
    },
    purchaseHistorySortDirection: {
      refreshModel: true,
    },
    purchaseHistoryPage: {
      refreshModel: true,
    },
    purchaseHistoryPageSize: { refreshModel: true },
    clientCode: {
      refreshModel: true,
    },
  };

  async beforeModel(transition: Transition): Promise<void> {
    await super.beforeModel(transition);
    const qp =
      (transition.to
        ?.queryParams as Partial<AppServicesWaldenGreenOverviewRouteQueryParams>) ??
      {};
    const user = await this.userService.getUser();
    const companies = user.clientCompanies ?? [];
    if (companies.length === 0) return;
    const requested = qp.clientCode;
    const hasValid =
      typeof requested === 'string' &&
      companies.some((c) => c.id === requested);
    if (!hasValid) {
      const clientCode = companies[0]!.id;
      this.routerService.replaceWith('app.services.walden-green.index', {
        queryParams: { ...qp, clientCode },
      });
    }
  }

  async model(params: AppServicesWaldenGreenOverviewRouteQueryParams) {
    const from = subMonths(
      Date.now(),
      rangeToMonthsMap[params.dateRange || '1year'],
    ).toISOString();
    const fromDateOnly = from.substring(0, 10);

    const to = new Date(Date.now()).toISOString();
    const toDateOnly = to.substring(0, 10);

    const hideDisclaimer =
      window.localStorage.getItem('hideDisclaimer') === 'true' ||
      config.environment === 'production';

    const canAccessLevel1 = this.userService.canAccessService(
      SERVICE_NAMES.WALDEN_GREEN_LEVEL1,
    );

    const canAccessLevel2 = this.userService.canAccessService(
      SERVICE_NAMES.WALDEN_GREEN_LEVEL2,
    );
    const canAccessLevel3 = this.userService.canAccessService(
      SERVICE_NAMES.WALDEN_GREEN_LEVEL3,
    );

    await this.fetchData.perform({
      from,
      fromDateOnly,
      toDateOnly,
      hideDisclaimer,
      params,
      canAccessLevel1,
      canAccessLevel2,
      canAccessLevel3,
    });

    return this.fetchData;
  }

  fetchData = task(
    async ({
      from,
      fromDateOnly,
      toDateOnly,
      hideDisclaimer,
      params,
      canAccessLevel1,
      canAccessLevel2,
      canAccessLevel3,
    }: {
      from: string;
      fromDateOnly: string;
      toDateOnly: string;
      hideDisclaimer: boolean;
      params: AppServicesWaldenGreenOverviewRouteQueryParams;
      canAccessLevel1: boolean;
      canAccessLevel2: boolean;
      canAccessLevel3: boolean;
    }) => {
      const level1Data = canAccessLevel1
        ? await this.fetchLevel1Data({
            fromDateOnly,
            toDateOnly,
            hideDisclaimer,
            params,
          })
        : null;

      const level2Data = canAccessLevel2
        ? await this.getLevel2Data(from)
        : null;

      const level3Data = canAccessLevel3
        ? await this.getLevel3Data(params)
        : null;

      return {
        ...level1Data,
        level2Data,
        level3Data,
      };
    },
  );

  async fetchLevel1Data({
    fromDateOnly,
    toDateOnly,
    hideDisclaimer,
    params,
  }: {
    fromDateOnly?: string;
    toDateOnly?: string;
    hideDisclaimer: boolean;
    params: AppServicesWaldenGreenOverviewRouteQueryParams;
  }) {
    const [user, emissionsDetailResponse, greenReport] = await Promise.all([
      this.userService.getUser(),
      this.fastApiService.get<EmissionDetailData>(
        '/walden-green/emissions/detailed',
        {
          ...(fromDateOnly && { fromDateInIso8601: fromDateOnly }),
          ...(toDateOnly && { toDateInIso8601: toDateOnly }),
          ...(params.clientCode && { clientCompanyId: params.clientCode }),
        },
      ),
      this.powerBiService.getTokenReportAndPages({
        reportInternalCode: 'WaldenGreen',
      }),
    ]);

    return {
      clientName: user.client || '',
      clientCompanies: user.clientCompanies ?? [],
      emissions: emissionsDetailResponse || ({} as EmissionDetailData),
      initialShowDisclaimer: !hideDisclaimer,
      greenReport,
    };
  }

  async getLevel3Data(params: AppServicesWaldenGreenOverviewRouteQueryParams) {
    let offsetPurchaseResponse = null;
    if (params.purchaseHistoryPage && params.purchaseHistoryPageSize) {
      offsetPurchaseResponse = await this.fastApiService.get<
        PaginatedResponse<OffsetPurchase>
      >('/walden-green/offset_purchases', {
        ...(params.purchaseHistorySortDirection &&
          params.purchaseHistorySortKey && {
            sortField: params.purchaseHistorySortKey,
            sortDirection: params.purchaseHistorySortDirection,
          }),
        page: params.purchaseHistoryPage,
        pageSize: params.purchaseHistoryPageSize,
      });
    }

    return {
      treePrice: 1.2,
      treePriceCurrency: 'EUR',
      offsetPurchases: offsetPurchaseResponse?.items || [],
      offsetPurchaseCount: offsetPurchaseResponse?.size || 0,
    };
  }

  async getLevel2Data(from: string) {
    const [offsetAggregated, offsetDetails] = await Promise.all([
      this.fastApiService.get<OffsetAggregated>(
        '/walden-green/offset/aggregated',
      ),
      this.fastApiService.get<OffsetDetailData[]>(
        '/walden-green/offset/details',
        {
          from,
        },
      ),
    ]);
    return {
      totalOffsetCo2Tonnes: {
        mtd: offsetAggregated?.totalOffsetCo2TonnesMtd || 0,
        ytd: offsetAggregated?.totalOffsetCo2TonnesYtd || 0,
      },
      totalTreesIncluded: {
        mtd: offsetAggregated?.totalTreesIncludedMtd || 0,
        ytd: offsetAggregated?.totalTreesIncludedYtd || 0,
      },
      totalTreesNonIncluded: {
        mtd: offsetAggregated?.totalTreesNonIncludedMtd || 0,
        ytd: offsetAggregated?.totalTreesNonIncludedYtd || 0,
      },
      treesPlanted: {
        mtd:
          (offsetAggregated?.totalTreesIncludedMtd || 0) +
          (offsetAggregated?.totalTreesNonIncludedMtd || 0),
        ytd:
          (offsetAggregated?.totalTreesIncludedYtd || 0) +
          (offsetAggregated?.totalTreesNonIncludedYtd || 0),
      },
      tonnesOfCo2: {
        mtd: offsetAggregated?.totalOffsetCo2TonnesMtd || 0,
        ytd: offsetAggregated?.totalOffsetCo2TonnesYtd || 0,
      },
      workingHours: {
        mtd: offsetAggregated?.totalWorkingHoursMtd || 0,
        ytd: offsetAggregated?.totalWorkingHoursYtd || 0,
      },
      offsets: offsetDetails || [],
    };
  }
}
